import { useState, useEffect } from 'react'
import { createPortal } from 'react-dom'
import { X, Loader2, Save } from 'lucide-react'
import { AvatarUploadField } from './AvatarUploadField'
import { HandleInput } from './HandleInput'
import { agentService } from '../services/agentService'
import { useUser } from '../contexts/UserContext'
import { useToast } from '../contexts/ToastContext'

interface FormData {
    name: string
    description: string
    handle: string
    function: string
    avatarFile?: File
}

interface FormErrors {
    name?: string
    description?: string
    handle?: string
    function?: string
    avatar?: string
    [key: string]: string | undefined
}

interface CreateAgentModalProps {
    isOpen: boolean
    onClose: () => void
    onAgentCreated?: (agent: { id: string; handle: string }) => void
}

export function CreateAgentModal({ isOpen, onClose, onAgentCreated }: CreateAgentModalProps) {
    const { user } = useUser()
    const { showToast } = useToast()

    const [formData, setFormData] = useState<FormData>({
        name: '',
        description: '',
        handle: '',
        function: ''
    })

    const [errors, setErrors] = useState<FormErrors>({})
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [isDirty, setIsDirty] = useState(false)

    // Reset form when modal opens
    useEffect(() => {
        if (isOpen) {
            setFormData({
                name: '',
                description: '',
                handle: '',
                function: ''
            })
            setErrors({})
            setIsDirty(false)
        }
    }, [isOpen])

    // Close modal with ESC key
    useEffect(() => {
        const handleEscape = (e: KeyboardEvent) => {
            if (e.key === 'Escape' && isOpen) {
                handleClose()
            }
        }

        document.addEventListener('keydown', handleEscape)
        return () => document.removeEventListener('keydown', handleEscape)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, isDirty])

    const validateForm = (): boolean => {
        const newErrors: FormErrors = {}
        let isValid = true

        // Only Nome and Handle are required
        if (!formData.name.trim()) {
            newErrors.name = 'Nome é obrigatório'
            isValid = false
        } else if (formData.name.length > 50) {
            newErrors.name = 'Nome muito longo (máx. 50 caracteres)'
            isValid = false
        }

        if (!formData.handle.trim()) {
            newErrors.handle = 'Handle é obrigatório'
            isValid = false
        }

        // Optional fields - only validate length if filled
        if (formData.function && formData.function.length > 100) {
            newErrors.function = 'Função muito longa (máx. 100 caracteres)'
            isValid = false
        }

        if (formData.description && formData.description.length > 300) {
            newErrors.description = 'Descrição muito longa (máx. 300 caracteres)'
            isValid = false
        }

        setErrors(newErrors)
        return isValid
    }

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()

        if (!validateForm()) return
        if (!user) {
            showToast('error', 'Usuário não autenticado')
            return
        }

        setIsSubmitting(true)
        try {
            const result = await agentService.createAgent(user.id, formData)
            showToast('success', 'Agente criado com sucesso!')

            // Call callback with agent data
            if (onAgentCreated && result) {
                onAgentCreated({
                    id: result.id,
                    handle: result.handle || ''
                })
            }

            onClose()
        } catch (error) {
            console.error('Error creating agent:', error)
            showToast('error', `Erro ao criar agente: ${error instanceof Error ? error.message : 'Tente novamente.'}`)
        } finally {
            setIsSubmitting(false)
        }
    }

    const handleChange = (field: keyof FormData, value: string | File) => {
        setFormData(prev => ({ ...prev, [field]: value }))
        setIsDirty(true)

        // Clear error when user types
        if (errors[field]) {
            setErrors(prev => ({ ...prev, [field]: undefined }))
        }
    }

    const handleClose = () => {
        if (isDirty && !isSubmitting) {
            const confirmed = window.confirm('Deseja descartar as alterações?')
            if (!confirmed) return
        }
        onClose()
    }


    console.log('CreateAgentModal: isOpen =', isOpen, 'will render:', isOpen)

    if (!isOpen) return null

    console.log('CreateAgentModal: Rendering modal NOW')

    const modalContent = (
        <>
            {/* Backdrop */}
            <div
                className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[9999] animate-in fade-in duration-200"
                onClick={handleClose}
            />

            {/* Modal */}
            <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 pointer-events-none">
                <div
                    className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden pointer-events-auto animate-in fade-in zoom-in-95 duration-200"
                    onClick={(e) => e.stopPropagation()}
                >
                    {/* Header */}
                    <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-800">
                        <div>
                            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                                Criar Novo Agente
                            </h2>
                            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                                Configure a identidade e propósito do seu novo assistente de IA.
                            </p>
                        </div>
                        <button
                            onClick={handleClose}
                            className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                            disabled={isSubmitting}
                        >
                            <X className="w-5 h-5" />
                        </button>
                    </div>

                    {/* Form */}
                    <form onSubmit={handleSubmit} className="overflow-y-auto max-h-[calc(90vh-140px)]">
                        <div className="px-6 py-6 space-y-6">
                            {/* Avatar Section */}
                            <div className="flex justify-center">
                                <AvatarUploadField
                                    value={formData.avatarFile}
                                    onChange={(file) => handleChange('avatarFile', file)}
                                    error={errors.avatar}
                                />
                            </div>

                            {/* Name Input */}
                            <div className="space-y-2">
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                                    Nome do Agente <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="text"
                                    data-testid="agent-name-input"
                                    value={formData.name}
                                    onChange={(e) => handleChange('name', e.target.value)}
                                    className={`
                                        block w-full px-4 py-2.5 rounded-xl border
                                        bg-white dark:bg-gray-900 text-gray-900 dark:text-white
                                        focus:ring-2 focus:ring-offset-0 transition-all
                                        ${errors.name
                                            ? 'border-red-300 focus:border-red-500 focus:ring-red-500/20'
                                            : 'border-gray-300 dark:border-gray-700 focus:border-purple-500 focus:ring-purple-500/20'
                                        }
                                    `}
                                    placeholder="Ex: Maria Silva"
                                    disabled={isSubmitting}
                                />
                                {errors.name && <p className="text-sm text-red-500">{errors.name}</p>}
                            </div>

                            {/* Handle Input */}
                            <HandleInput
                                value={formData.handle}
                                onChange={(value) => handleChange('handle', value)}
                                error={errors.handle}
                                onError={(error) => setErrors(prev => ({ ...prev, handle: error }))}
                            />

                            {/* Function Input */}
                            <div className="space-y-2">
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                                    Função / Cargo
                                </label>
                                <input
                                    type="text"
                                    value={formData.function}
                                    onChange={(e) => handleChange('function', e.target.value)}
                                    className={`
                                        block w-full px-4 py-2.5 rounded-xl border
                                        bg-white dark:bg-gray-900 text-gray-900 dark:text-white
                                        focus:ring-2 focus:ring-offset-0 transition-all
                                        ${errors.function
                                            ? 'border-red-300 focus:border-red-500 focus:ring-red-500/20'
                                            : 'border-gray-300 dark:border-gray-700 focus:border-purple-500 focus:ring-purple-500/20'
                                        }
                                    `}
                                    placeholder="Ex: Assistente de Marketing"
                                    disabled={isSubmitting}
                                />
                                {errors.function && <p className="text-sm text-red-500">{errors.function}</p>}
                            </div>

                            {/* Description Textarea */}
                            <div className="space-y-2">
                                <div className="flex justify-between">
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                                        Descrição
                                    </label>
                                    <span className={`text-xs ${formData.description.length > 300 ? 'text-red-500' : 'text-gray-400'}`}>
                                        {formData.description.length}/300
                                    </span>
                                </div>
                                <textarea
                                    value={formData.description}
                                    onChange={(e) => handleChange('description', e.target.value)}
                                    rows={4}
                                    className={`
                                        block w-full px-4 py-3 rounded-xl border resize-none
                                        bg-white dark:bg-gray-900 text-gray-900 dark:text-white
                                        focus:ring-2 focus:ring-offset-0 transition-all
                                        ${errors.description
                                            ? 'border-red-300 focus:border-red-500 focus:ring-red-500/20'
                                            : 'border-gray-300 dark:border-gray-700 focus:border-purple-500 focus:ring-purple-500/20'
                                        }
                                    `}
                                    placeholder="Descreva o propósito e personalidade deste agente..."
                                    disabled={isSubmitting}
                                />
                                {errors.description && <p className="text-sm text-red-500">{errors.description}</p>}
                            </div>
                        </div>

                        {/* Footer */}
                        <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-800 flex items-center justify-end gap-3">
                            <button
                                type="button"
                                onClick={handleClose}
                                className="px-6 py-2.5 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-xl transition-colors"
                                disabled={isSubmitting}
                            >
                                Cancelar
                            </button>
                            <button
                                type="submit"
                                data-testid="submit-agent-btn"
                                disabled={isSubmitting}
                                className="
                                    flex items-center gap-2 px-8 py-2.5 
                                    bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 
                                    text-white font-medium rounded-xl shadow-lg shadow-purple-500/20 
                                    transition-all disabled:opacity-70 disabled:cursor-not-allowed
                                "
                            >
                                {isSubmitting ? (
                                    <>
                                        <Loader2 className="w-4 h-4 animate-spin" />
                                        Criando...
                                    </>
                                ) : (
                                    <>
                                        <Save className="w-4 h-4" />
                                        Criar Agente
                                    </>
                                )}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </>
    )

    return createPortal(modalContent, document.body)
}
